import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AuthService } from '../auth.service';
import { BaseApiResponse } from '../models/api-response';
import { MyPermissions } from '../models/permissions';

const EMPTY: MyPermissions = { permissions: [], canManagePermissions: false, catalog: [] };

/**
 * What the signed-in user is allowed to do, from GET /api/admin/permissions.
 *
 * Screens use this to show or hide actions. The server enforces the same
 * rules, so hiding a button is for clarity, not security.
 *
 * The result is cached per access token: one request per session, and a new
 * request automatically after logging in as someone else.
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private cachedFor: string | null = null;
  private cached$: Observable<MyPermissions> | null = null;

  getMine(): Observable<MyPermissions> {
    const token = this.authService.getAccessToken();
    if (!this.cached$ || this.cachedFor !== token) {
      this.cachedFor = token;
      this.cached$ = this.http
        .get<BaseApiResponse<MyPermissions>>(`${environment.baseAPI}/admin/permissions`)
        .pipe(
          map((res) => ({ ...EMPTY, ...(res?.data ?? {}) })),
          // On failure, behave as "no permissions" and allow a retry next time.
          catchError(() => {
            this.cached$ = null;
            return of(EMPTY);
          }),
          shareReplay(1),
        );
    }
    return this.cached$;
  }

  /** True when the user holds the given permission key. */
  has(permission: string): Observable<boolean> {
    return this.getMine().pipe(map((mine) => mine.permissions.includes(permission)));
  }
}
